import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InternalPageLayout from '../components/InternalPageLayout';
import { FileText, BookOpen, Calendar, Download, Search, Filter, ArrowRight, ExternalLink } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getFileUrl, getBackendURL } from '../utils/assetUrlHelper';

const API_URL = getBackendURL();

export default function PublicationsPage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('annual-reports');
  
  // Data States
  const [annualReports, setAnnualReports] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [udyanRashmi, setUdyanRashmi] = useState([]);
  const [institutePubs, setInstitutePubs] = useState([]);
  const [ePubs, setEPubs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportsRes, newslettersRes, rashmiRes, cishRes, ePubsRes] = await Promise.all([
        axios.get(`${API_URL}/api/publications/annual-report`),
        axios.get(`${API_URL}/api/publications/newsletter`),
        axios.get(`${API_URL}/api/publications/udyan-rashmi`),
        axios.get(`${API_URL}/api/publications/cish-publications`),
        axios.get(`${API_URL}/api/publications/e-publications`)
      ]);

      setAnnualReports(reportsRes.data || []);
      setNewsletters(newslettersRes.data || []);
      setUdyanRashmi(rashmiRes.data || []);
      setInstitutePubs(cishRes.data?.institute_publications || []);
      setEPubs(ePubsRes.data || []);
    } catch (error) {
      console.error('Error fetching publications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = (data) => {
    if (!searchQuery) return data;
    const lowerQuery = searchQuery.toLowerCase();
    return data.filter(item => 
      (item.title && item.title.toLowerCase().includes(lowerQuery)) ||
      (item.description && item.description.toLowerCase().includes(lowerQuery)) ||
      (item.year && item.year.toString().includes(lowerQuery))
    );
  };

  const renderPublicationCard = (pub, type) => (
    <Card key={pub.id} className="h-full flex flex-col hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start gap-2">
          <Badge variant="outline" className="mb-2 bg-green-50 text-green-700 border-green-200">
            {pub.year || pub.date || type}
          </Badge>
          {pub.is_new && <Badge className="bg-red-500">NEW</Badge>}
        </div>
        <CardTitle className="text-lg font-bold text-gray-800 line-clamp-2" title={pub.title}>
          {pub.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-grow pb-4">
        {pub.description && (
          <div 
            className="text-sm text-gray-600 line-clamp-3 mb-2"
            dangerouslySetInnerHTML={{ __html: pub.description }}
          />
        )}
        {pub.authors && <p className="text-xs text-gray-500 italic">By: {pub.authors}</p>}
      </CardContent>
      <CardFooter className="pt-0">
        {pub.file_url || pub.url || pub.link ? (
          <Button 
            className="w-full bg-[#0E6D2B] hover:bg-[#0A5421]" 
            asChild
          >
            <a href={getFileUrl(pub.file_url || pub.url || pub.link)} target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4 mr-2" />
              Download / View
            </a>
          </Button>
        ) : (
             <Button variant="secondary" className="w-full" disabled>
               No File Available
             </Button>
        )}
      </CardFooter>
    </Card>
  );

  const renderGrid = (data, type) => {
    const filtered = getFilteredData(data);
    if (filtered.length === 0) {
      return (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500">No publications found.</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map(item => renderPublicationCard(item, type))}
      </div>
    );
  };

  return (
    <InternalPageLayout
      title="Publications"
      subtitle="Research papers, annual reports, newsletters, and technical bulletins from CISH"
      breadcrumbs={[{ label: 'Publications' }]}
    >
      <div className="mb-8 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input 
            placeholder="Search publications..." 
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="text-sm text-gray-500">
           Total Publications: {annualReports.length + newsletters.length + udyanRashmi.length + institutePubs.length + ePubs.length}
        </div>
      </div>

      <Tabs defaultValue="annual-reports" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto mb-8 bg-[#F4F8EF] p-1">
          <TabsTrigger value="annual-reports" className="data-[state=active]:bg-[#0E6D2B] data-[state=active]:text-white">Annual Reports</TabsTrigger>
          <TabsTrigger value="newsletters" className="data-[state=active]:bg-[#0E6D2B] data-[state=active]:text-white">Newsletters</TabsTrigger>
          <TabsTrigger value="udyan-rashmi" className="data-[state=active]:bg-[#0E6D2B] data-[state=active]:text-white">Udyan Rashmi</TabsTrigger>
          <TabsTrigger value="institute-pubs" className="data-[state=active]:bg-[#0E6D2B] data-[state=active]:text-white">Institute Pubs</TabsTrigger>
          <TabsTrigger value="e-pubs" className="data-[state=active]:bg-[#0E6D2B] data-[state=active]:text-white">E-Publications</TabsTrigger>
        </TabsList>

        <TabsContent value="annual-reports" className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-6 h-6 text-[#0E6D2B]" />
            <h2 className="text-2xl font-bold text-gray-800">Annual Reports</h2>
          </div>
          {renderGrid(annualReports, 'Annual Report')}
        </TabsContent>

        <TabsContent value="newsletters" className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
             <FileText className="w-6 h-6 text-[#0E6D2B]" />
            <h2 className="text-2xl font-bold text-gray-800">CISH Newsletters</h2>
          </div>
          {renderGrid(newsletters, 'Newsletter')}
        </TabsContent>

        <TabsContent value="udyan-rashmi" className="space-y-6">
           <div className="flex items-center gap-2 mb-4">
             <FileText className="w-6 h-6 text-[#0E6D2B]" />
            <h2 className="text-2xl font-bold text-gray-800">Udyan Rashmi (Hindi Magazine)</h2>
          </div>
          {renderGrid(udyanRashmi, 'Magazine')}
        </TabsContent>

        <TabsContent value="institute-pubs" className="space-y-6">
           <div className="flex items-center gap-2 mb-4">
             <BookOpen className="w-6 h-6 text-[#0E6D2B]" />
            <h2 className="text-2xl font-bold text-gray-800">Institute Publications & Books</h2>
          </div>
          {renderGrid(institutePubs, 'Book')}
        </TabsContent>

        <TabsContent value="e-pubs" className="space-y-6">
           <div className="flex items-center gap-2 mb-4">
             <Download className="w-6 h-6 text-[#0E6D2B]" />
            <h2 className="text-2xl font-bold text-gray-800">E-Publications</h2>
          </div>
          {renderGrid(ePubs, 'E-Pub')}
        </TabsContent>
      </Tabs>
    </InternalPageLayout>
  );
}
